import { motion } from 'framer-motion';
import {
  Compass,
  Layers,
  Radio,
  ShieldCheck,
  FlaskConical,
  Gauge,
  type LucideIcon,
} from 'lucide-react';
import Container from './Container';
import SectionHeading from './SectionHeading';
import { fadeUp, stagger, viewportOnce } from '@/lib/motion';
import { capabilities } from '@/lib/content';

const iconMap: Record<string, LucideIcon> = {
  compass: Compass,
  layers: Layers,
  radio: Radio,
  shield: ShieldCheck,
  flask: FlaskConical,
  gauge: Gauge,
};

export default function Capabilities() {
  return (
    <section id="capabilities" aria-labelledby="capabilities-heading" className="py-24 md:py-36">
      <Container>
        <SectionHeading
          eyebrow="Capabilities"
          title={
            <span id="capabilities-heading">
              A small firm <span className="text-ink/50">that covers</span> the full stack of a
              modern business.
            </span>
          }
          lede="Work spans strategy, software, media, and operations. Engagements stay focused, senior, and accountable to outcomes."
        />

        <motion.ul
          variants={stagger(0.1, 0.06)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 md:grid-cols-2 lg:grid-cols-3"
        >
          {capabilities.map((cap) => {
            const Icon = iconMap[cap.icon] ?? Compass;
            return (
              <motion.li
                key={cap.title}
                variants={fadeUp}
                className="group relative bg-paper p-7 transition-colors duration-300 hover:bg-paper-50 md:p-9"
              >
                <div className="flex items-start gap-5">
                  <div className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-ink/10 bg-paper-50 text-accent transition-colors group-hover:border-accent/40 group-hover:text-accent-700">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-xl tracking-tight md:text-[1.375rem]">
                      {cap.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-ink/70">{cap.body}</p>
                  </div>
                </div>
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-gradient-to-r from-transparent via-accent/50 to-transparent transition-transform duration-500 group-hover:scale-x-100"
                />
              </motion.li>
            );
          })}
        </motion.ul>
      </Container>
    </section>
  );
}
